// Ajustar os 4 cantos da folha (Scanner PDF, 0.49). Arraste cada bolinha até a ponta da folha:
// o app recorta e endireita a página, mesmo que a foto tenha sido tirada de lado.
import { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Image, Modal, PanResponder, Pressable, Text, View } from 'react-native';
import Svg, { Polygon } from 'react-native-svg';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { Cantos } from '../lib/scanImagem';
import { CANTOS_INTEIROS } from '../lib/scanImagem';
import { tamanhoDe } from '../lib/scanArquivos';
import { t } from '../lib/i18n';

const MARGEM: Cantos = [{ x: 0.06, y: 0.06 }, { x: 0.94, y: 0.06 }, { x: 0.94, y: 0.94 }, { x: 0.06, y: 0.94 }];

export function AjustarCantos({ uri, inicial, cor, titulo, onCancelar, onAplicar }: {
  uri: string | null; inicial?: Cantos | null; cor: string; titulo?: string; onCancelar: () => void; onAplicar: (c: Cantos | null) => void;
}) {
  const ins = useSafeAreaInsets();
  const [tam, setTam] = useState<{ w: number; h: number } | null>(null);
  const [area, setArea] = useState<{ w: number; h: number } | null>(null);
  const [c, setC] = useState<Cantos>(inicial ?? MARGEM);
  const cRef = useRef(c); cRef.current = c;
  useEffect(() => {
    if (!uri) return;
    setTam(null); setC(inicial ?? MARGEM);
    tamanhoDe(uri).then(setTam).catch(() => setTam({ w: 3, h: 4 }));
  }, [uri]); // eslint-disable-line react-hooks/exhaustive-deps

  // a imagem inteira cabe na área (como "contain")
  const caixa = useMemo(() => {
    if (!tam || !area) return null;
    const k = Math.min(area.w / tam.w, area.h / tam.h);
    const w = tam.w * k, h = tam.h * k;
    return { w, h, x: (area.w - w) / 2, y: (area.h - h) / 2 };
  }, [tam, area]);
  const caixaRef = useRef(caixa); caixaRef.current = caixa;

  const pans = useMemo(() => [0, 1, 2, 3].map(i => {
    let ini = { x: 0, y: 0 };
    return PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderTerminationRequest: () => false,
      onPanResponderGrant: () => { ini = { ...cRef.current[i] }; },
      onPanResponderMove: (_, g) => {
        const cx = caixaRef.current; if (!cx) return;
        const x = Math.max(0, Math.min(1, ini.x + g.dx / cx.w)), y = Math.max(0, Math.min(1, ini.y + g.dy / cx.h));
        setC(v => { const n = [...v] as Cantos; n[i] = { x, y }; return n; });
      },
    });
  }), []);

  return (
    <Modal visible={!!uri} animationType="slide" onRequestClose={onCancelar}>
      <View style={{ flex: 1, backgroundColor: '#0B0B0F', paddingTop: ins.top + 6, paddingBottom: ins.bottom + 10 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 10 }}>
          <Pressable onPress={onCancelar} hitSlop={12}><Text style={{ color: '#bbb', fontSize: 15 }}>{t('Cancelar')}</Text></Pressable>
          <Text style={{ flex: 1, textAlign: 'center', color: '#fff', fontWeight: '700', fontSize: 16 }}>{titulo ?? t('Ajustar a folha')}</Text>
          <Pressable onPress={() => onAplicar(c)} hitSlop={12}><Text style={{ color: cor, fontWeight: '800', fontSize: 15 }}>{t('Aplicar')}</Text></Pressable>
        </View>
        <Text style={{ color: '#aaa', textAlign: 'center', fontSize: 13, paddingHorizontal: 20, marginBottom: 8 }}>
          {t('Arraste as bolinhas até as 4 pontas da folha. O app recorta e endireita a página.')}
        </Text>
        <View style={{ flex: 1, margin: 18 }} onLayout={e => setArea({ w: e.nativeEvent.layout.width, h: e.nativeEvent.layout.height })}>
          {!caixa && <ActivityIndicator color={cor} style={{ marginTop: 60 }} />}
          {caixa && uri && (
            <View style={{ position: 'absolute', left: caixa.x, top: caixa.y, width: caixa.w, height: caixa.h }}>
              <Image source={{ uri }} style={{ width: caixa.w, height: caixa.h }} resizeMode="stretch" />
              <Svg width={caixa.w} height={caixa.h} style={{ position: 'absolute', left: 0, top: 0 }} pointerEvents="none">
                <Polygon points={c.map(p => `${p.x * caixa.w},${p.y * caixa.h}`).join(' ')} fill={cor} fillOpacity={0.16} stroke={cor} strokeWidth={2.5} />
              </Svg>
              {c.map((p, i) => (
                <View key={i} {...pans[i].panHandlers} hitSlop={{ top: 14, bottom: 14, left: 14, right: 14 }}
                  style={{ position: 'absolute', left: p.x * caixa.w - 18, top: p.y * caixa.h - 18, width: 36, height: 36, borderRadius: 18,
                    borderWidth: 3, borderColor: '#fff', backgroundColor: cor, opacity: 0.92 }} />
              ))}
            </View>
          )}
        </View>
        <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 12, paddingHorizontal: 16 }}>
          <Pressable onPress={() => setC(CANTOS_INTEIROS)} style={{ paddingHorizontal: 16, paddingVertical: 10, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.12)' }}>
            <Text style={{ color: '#fff' }}>⤢ {t('Foto inteira')}</Text>
          </Pressable>
          <Pressable onPress={() => setC(MARGEM)} style={{ paddingHorizontal: 16, paddingVertical: 10, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.12)' }}>
            <Text style={{ color: '#fff' }}>↺ {t('Recomeçar')}</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}
